import { useState, useRef } from "react";
import { AddModal } from "@/pages/portal/faqs/components/add-modal";
import {
  Button,
  Icon,
  useToast,
  Badge,
  Avatar,
  AvatarFallback,
  Heading,
} from "@elaniintc/goes-design-system";
import { useSyncNpePayments } from "@/queries/payment/payment.api";

interface FileUpload {
  id: string;
  file: File;
  progress: number;
  status: "uploading" | "completed" | "error";
}

interface NpePaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NpePaymentsModal = ({
  isOpen,
  onClose,
}: NpePaymentsModalProps) => {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const { mutateAsync: syncNpePayments, isPending } = useSyncNpePayments();

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (file.name.endsWith(".txt")) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      showToast({
        title: "Formato inválido",
        description: `Solo se permiten archivos .txt`,
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }

    if (validFiles.length > 0) {
      const newFiles: FileUpload[] = validFiles.map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        progress: 0,
        status: "uploading",
      }));

      setFiles((prev) => [...prev, ...newFiles]);

      // Subir cada archivo
      newFiles.forEach((fileUpload) => {
        uploadFile(fileUpload.id, fileUpload.file);
      });
    }
  };

  const uploadFile = async (fileId: string, file: File) => {
    try {
      // Simular progreso mientras se sube
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += Math.random() * 20;
        if (progress < 90) {
          setFiles((prev) =>
            prev.map((f) => (f.id === fileId ? { ...f, progress } : f)),
          );
        }
      }, 150);

      // Subir archivo
      await syncNpePayments(file);

      // Completar progreso
      clearInterval(progressInterval);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, progress: 100, status: "completed" } : f,
        ),
      );
    } catch {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId ? { ...f, status: "error", progress: 0 } : f,
        ),
      );

      showToast({
        title: "Error al subir archivo",
        description: "No se pudo procesar el archivo. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleUpdate = () => {
    const completedFiles = files.filter((f) => f.status === "completed");

    if (completedFiles.length === 0) {
      showToast({
        title: "No hay archivos completados",
        description: "Espera a que los archivos terminen de subir",
        options: {
          type: "error",
          duration: 4000,
        },
      });
      return;
    }

    showToast({
      title: "Pagos con NPE actualizados",
      description:
        "Los pagos detallados en el archivo han sido actualizados para poder verificar su estatus en el sistema",
      options: {
        type: "success",
        duration: 4000,
      },
    });

    // Limpiar archivos y cerrar modal
    setFiles([]);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    return `${Math.round(bytes / 1024)} KB`;
  };

  const allFilesCompleted =
    files.length > 0 && files.every((f) => f.status === "completed");
  const hasUploadingFiles = files.some((f) => f.status === "uploading");
  const hasErrorFiles = files.some((f) => f.status === "error");

  return (
    <AddModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="max-w-[620px]"
      header={
        <div className="flex items-center justify-between px-8 py-3 border-b border-neutral-silent">
          <Heading variant="h5" className="!mb-0">
            Actualizar pagos NPE
          </Heading>
          <Button
            type="button"
            variant="quiet"
            size="icon"
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <Icon type="fas" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="px-8 pb-6 pt-4 border-t border-neutral-silent">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="tertiary"
              className="flex-1 font-semibold"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="flex-1 font-semibold"
              onClick={handleUpdate}
              disabled={
                !allFilesCompleted ||
                hasUploadingFiles ||
                hasErrorFiles ||
                isPending
              }
            >
              Actualizar
            </Button>
          </div>
        </div>
      }
    >
      <div className="px-8 py-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-heading mb-1">
            Comprobantes de pagos por NPE
            <span className="text-danger">*</span>
          </label>
          <p className="text-sm text-body mb-3">
            El archivo actualizará el sistema para poder verificar el estatus de
            todos los pagos especificados.
          </p>

          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-neutral bg-surface-action-accent-quiet"
                : "border-neutral bg-neutral-object"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".txt"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-2">
              <Icon
                name="arrow-up-from-bracket"
                type="fas"
                className="h-6 w-6 text-body"
              />
              <p className="text-sm text-body">
                Arrastra y Suelta o{" "}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-action-quiet hover:text-action-accent-quiet font-medium underline"
                >
                  Selecciona un archivo
                </button>
              </p>
              <p className="text-xs text-body">Subir archivos .txt</p>
            </div>
          </div>
        </div>

        {/* Lista de archivos */}
        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((fileUpload) => (
              <div
                key={fileUpload.id}
                className="border border-neutral-quiet rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <Avatar shape="square" size="xs">
                    <AvatarFallback color="neutral" type="icon" shape="square">
                      <Icon type="fas" name="file" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm font-normal text-body truncate">
                        {fileUpload.file.name}
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {fileUpload.status === "uploading" && (
                          <>
                            <Badge
                              variant="neutral"
                              size="default"
                              shape="pill"
                            >
                              {formatFileSize(fileUpload.file.size)}
                            </Badge>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => handleRemoveFile(fileUpload.id)}
                              className="size-8"
                            >
                              <Icon type="fas" name="times" />
                            </Button>
                          </>
                        )}
                        {fileUpload.status === "completed" && (
                          <>
                            <Badge
                              variant="neutral"
                              size="default"
                              shape="pill"
                            >
                              <Icon
                                type="fas"
                                name="circle-info"
                                className="text-current"
                              />
                              {formatFileSize(fileUpload.file.size)}
                            </Badge>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => handleRemoveFile(fileUpload.id)}
                              className="size-8"
                            >
                              <Icon type="far" name="trash" />
                            </Button>
                          </>
                        )}
                        {fileUpload.status === "error" && (
                          <>
                            <Badge variant="danger" size="default" shape="pill">
                              <Icon
                                type="fas"
                                name="circle-exclamation"
                                className="text-current"
                              />
                              Error
                            </Badge>
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => handleRemoveFile(fileUpload.id)}
                              className="size-8"
                            >
                              <Icon type="far" name="trash" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {fileUpload.status === "uploading" && (
                      <div className="w-full bg-stroke-quiet rounded-full h-1.5">
                        <div
                          className="bg-action-quiet h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${fileUpload.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AddModal>
  );
};
